// durable-file-writer.ts
//
// Ghi file BỀN theo §3.4: tmp → ghi đủ byte (lặp tới khi hết) → fsync fd → rename
// → fsync THƯ MỤC. Chỉ khi cả chuỗi này xong mới được coi là "đã ghi" (ack/publish).
//
// ⚠️ KHÔNG sửa `writeTextAtomic`/`writeJsonAtomic` trong filesystem-helpers.ts:
// hai hàm đó atomic (rename) nhưng KHÔNG fsync ⇒ mất dữ liệu khi máy mất điện sau
// rename. Đường interactive giữ nguyên hành vi cũ (§21 additive-only); autopilot
// dùng primitive mới này.
//
// Vì sao fsync thư mục: rename chỉ sửa dir entry; nếu dir entry chưa xuống đĩa thì
// sau crash file có thể biến mất dù nội dung đã fsync. Dir fsync CHỈ cần khi
// tạo/replace/truncate — KHÔNG cần mỗi lần append (dir entry không đổi).
//
// macOS: libuv hiện thực `fs.fsync` bằng `fcntl(F_FULLFSYNC)` rồi fallback `fsync`
// — xem evidence report phase-03 (đo trên Node đang dùng).

import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import type { FileHandle } from "node:fs/promises";
import { dirname } from "node:path";

/** Seam bơm được để test order-of-operations và mô phỏng lỗi (không mock cả `fs`). */
export interface DurableFsOps {
  open: (path: string, flags: string, mode?: number) => Promise<FileHandle>;
  write: (handle: FileHandle, buffer: Buffer, offset: number) => Promise<{ bytesWritten: number }>;
  fsyncFile: (handle: FileHandle) => Promise<void>;
  close: (handle: FileHandle) => Promise<void>;
  rename: (from: string, to: string) => Promise<void>;
  unlink: (path: string) => Promise<void>;
  fsyncDir: (dirPath: string) => Promise<void>;
}

export const realDurableFsOps: DurableFsOps = {
  open: (path, flags, mode) => fs.open(path, flags, mode),
  write: (handle, buffer, offset) => handle.write(buffer, offset, buffer.length - offset),
  fsyncFile: (handle) => handle.sync(),
  close: (handle) => handle.close(),
  rename: (from, to) => fs.rename(from, to),
  unlink: (path) => fs.unlink(path),
  fsyncDir: async (dirPath) => {
    // Mở thư mục read-only rồi fsync fd của nó. Trên macOS/Linux hợp lệ; nếu
    // platform không cho (Windows) thì caller nuốt lỗi ở tầng trên.
    const handle = await fs.open(dirPath, "r");
    try {
      await handle.sync();
    } finally {
      await handle.close();
    }
  },
};

export interface WriteFileDurableOptions {
  ops?: DurableFsOps;
  mode?: number;
}

/**
 * Ghi đủ `buffer.length` byte vào `handle`, LẶP khi write ngắn.
 * `write(2)` được phép trả về ít hơn số byte yêu cầu; bỏ vòng lặp = file cụt âm thầm.
 */
export async function writeAllBytes(
  handle: FileHandle,
  buffer: Buffer,
  ops: DurableFsOps = realDurableFsOps,
): Promise<void> {
  let offset = 0;
  while (offset < buffer.length) {
    const { bytesWritten } = await ops.write(handle, buffer, offset);
    if (bytesWritten <= 0) {
      throw new Error(
        `writeAllBytes: write trả về ${bytesWritten} byte tại offset ${offset}/${buffer.length} — không tiến triển.`,
      );
    }
    offset += bytesWritten;
  }
}

/**
 * Ghi bền `bytes` vào `path`. Lỗi ở BẤT KỲ bước nào ⇒ throw và file gốc GIỮ NGUYÊN
 * (mọi thao tác diễn ra trên tmp cho tới `rename`).
 */
export async function writeFileDurable(
  path: string,
  bytes: Buffer,
  options: WriteFileDurableOptions = {},
): Promise<void> {
  const ops = options.ops ?? realDurableFsOps;
  const dir = dirname(path);
  const tmpPath = `${path}.tmp-${process.pid}-${randomUUID()}`;

  let handle: FileHandle | undefined;
  try {
    handle = await ops.open(tmpPath, "wx", options.mode);
    await writeAllBytes(handle, bytes, ops);
    await ops.fsyncFile(handle);
    await ops.close(handle);
    handle = undefined;
    await ops.rename(tmpPath, path);
  } catch (err) {
    if (handle !== undefined) await ops.close(handle).catch(() => {});
    await ops.unlink(tmpPath).catch(() => {});
    throw err;
  }
  // Sau khi dir entry đổi (create/replace) mới fsync thư mục.
  await ops.fsyncDir(dir);
}

/** Tiện ích: fsync thư mục (dùng sau truncate/create ở event-journal & recovery). */
export async function fsyncDirectory(
  dirPath: string,
  ops: DurableFsOps = realDurableFsOps,
): Promise<void> {
  await ops.fsyncDir(dirPath);
}
